'use strict';

import moment from "moment";
import { action, configure, flow, observable, observe, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {getAggregateState} from "@uw-it-sis/lib-react/lib/StateUtils";
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import {isTrue} from "@uw-it-sis/lib-react/lib/Utils";

import noticesStore from '@uw-it-sis/lib-react-myplan/lib/NoticesStore';

import registrationStore from "../registration/RegistrationStore";

import Cookies from 'js-cookie';

const SB_ONBOARDING_FLAG_COOKIE = "myplan.sb_onboarding_flag";

class ScheduleBuilderStore {
    state = States.initial;

    /**
     * Tracks whether or not the user has viewed the schedule builder onboarding dialog, based on a cookie
     * @type {boolean}
     */
    onboardingFlag = false;

    constructor() {
        makeObservable(this, {
            state: observable,
            onboardingFlag: observable,
            mount: action,
            checkDataLoadStates: action
        });

        this.componentKey = "ScheduleBuilder";

        this.termId = null;

        this.dataLoadStates = {
            currentPlanableTerm: States.initial,
            publishedTerms: States.initial,
            notices: States.initial
        };
    }

    mount() {
        this.dataLoadStates = {
            currentPlanableTerm: States.pending,
            publishedTerms: States.pending,
            notices: States.pending
        };

        this.noticesDisposer = observe(noticesStore, "state", function (change) {
            this.dataLoadStates.notices = change.newValue;
            this.checkDataLoadStates();
        }.bind(this));

        noticesStore.mount();

        this.termsDisposer = observe(termStore, "state", function(change) {
            this.dataLoadStates.publishedTerms = change.newValue;
            this.dataLoadStates.currentPlanableTerm = change.newValue;
            this.checkDataLoadStates();
        }.bind(this));

        // Load published terms for SB navigation between terms
        termStore.load(['currentPlanableTerm','publishedTerms']);

        // Get the sb onboarding flag from the cookie. If there is no cookie use the default
        this.onboardingFlag = isTrue(Cookies.get(SB_ONBOARDING_FLAG_COOKIE)) ?? this.onboardingFlag;

        this.checkDataLoadStates();
    }

    unmount() {
        this.termsDisposer();
        // this.currentPlanableTermStoreDisposer();
        this.noticesDisposer();
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    // Based on URL figure out which term to display, if not is provided current planable is default
    calculateDisplayTerm(urlTerm) {
        let result = registrationStore.calculateDisplayTerm(urlTerm);

        if (!result.termId) {
            const currentPlanableTerm = termStore.get('currentPlanableTerm');
            result.termId = currentPlanableTerm.id;
            result.isPublished = true;
            result.errorMsgs = [];
        }

        this.termId = result.termId;

        return result;
    }

    // Based on current term id, return an object of next and previous terms to navigate to.
    // If there is no term to navigate to, property is undefined which UI makes a disabled element.
    // Typically only 2 terms ever published.
    getNavigation(termId) {
        let result = {
            prev: undefined,
            next: undefined,
        }

        let publishedTerms = termStore.get('publishedTerms');

        let publishedTermsList = publishedTerms.map(t => t.id);
        publishedTermsList.sort();

        let isCurrentTermOnlyPublished = (publishedTermsList.length === 1 && publishedTermsList.indexOf(termId) === 0);

        if (!isCurrentTermOnlyPublished) {
            let position = publishedTermsList.indexOf(termId);
            if (position > 0) {
                result.prev = publishedTermsList[(position - 1)];
            }
            if (position + 1 < publishedTermsList.length) {
                result.next = publishedTermsList[(position +1)];
            }
        }

        return result;
    }

    /**
     * Calculates the start date for the schedule builder calendar week view.
     * Week view starts with a Sunday (even though not shown). If first day of
     * class is a Monday, then the date returned is the Sunday before first day.
     * If first day is something other then Monday, then date returned is the Sunday
     * after the first day.
     * @return {string} date which calendar starts with in YYYY-MM-DD format.
     */
    getInitialDate() {
        // Get list of published terms
        let publishedTerms = termStore.get('publishedTerms');
        // Find the index of term that is currently being viewed
        let termForViewIndex = publishedTerms.findIndex(term => term.id === this.termId);
        // Get the obj for the term in current view
        let termForView = publishedTerms[termForViewIndex];
        // Find the "FirstDay" of class index in the list of academic calendar
        let firstDayIndex = termForView.academicCalendar.findIndex(event => event.key === "FirstDay");
        // Create a moment obj for the first day of classes
        let firstDay = moment(termForView.academicCalendar[firstDayIndex].date, 'YYYY-MM-DD');

        let initialDate;
        // Check is first day is a Monday
        if (firstDay.weekday() === 1) {
            // return Sunday before first day when first day is Monday
            initialDate = firstDay.subtract(1, 'days');
        } else {
            // return Sunday after first day when first day is not a Monday
            initialDate = firstDay.endOf('week').add(1, 'days');
        }

        return initialDate.format('YYYY-MM-DD');
    }

    /**
     * Sets the value of the onboarding flag to the opposite value, then updates the same flag on the server
     * which populates a cookie.
     * No need to yield for this operation.
     *
     * @param onboardingFlag the new value of the flag
     */
    toggleOnboardingFlag = flow(function* () {

        this.onboardingFlag = !this.onboardingFlag;

        // Set the sb flag cookie. TTL is 1 year
        let sbCookieExpiration = 365; // expires is in days
        Cookies.set(SB_ONBOARDING_FLAG_COOKIE, this.onboardingFlag, { expires: sbCookieExpiration, sameSite: 'strict', secure: true });

        if (appStore.isDebug()) {
            console.log(`ScheduleBuilderStore: Updating onboarding flag in ${SB_ONBOARDING_FLAG_COOKIE} cookie to ${this.onboardingFlag}`);
        }

    }.bind(this));

}

export default new ScheduleBuilderStore();